import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Clock, Boxes, Loader2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { teamApi } from "../../lib/api";
import { URGENCY_LEVELS } from "../../lib/checklistTemplate";

const blank = { part_item: "", quantity: 1, urgency: "normal", notes: "", requested_by: "" };

export const TeamStock = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await teamApi.listStockRequests();
      setRequests(r);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.part_item.trim()) {
      toast.error("Enter the part or item you need");
      return;
    }
    setBusy(true);
    try {
      await teamApi.createStockRequest(form);
      toast.success("Request sent — admin notified");
      setForm(blank);
      load();
    } catch {
      toast.error("Could not send request");
    } finally { setBusy(false); }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const completed = requests.filter((r) => r.status !== "pending");

  return (
    <div data-testid="team-stock-page">
      <h1 className="font-display font-black uppercase text-2xl sm:text-3xl text-[#111619]">Stock Requests</h1>
      <p className="text-sm text-[#3f4b52]">Pending requests stay visible until admin confirms.</p>

      <form onSubmit={submit} className="mt-5 bg-white border border-[#cfd7db] rounded-md p-4 space-y-3" data-testid="stock-form">
        <div>
          <Label className="text-xs uppercase text-[#3f4b52]">Part / item required *</Label>
          <Input
            value={form.part_item}
            onChange={(e) => setForm({ ...form, part_item: e.target.value })}
            placeholder="e.g. Toyota Hilux windscreen mouldings"
            className="mt-1.5 bg-white border-[#cfd7db] h-11"
            data-testid="stock-item"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase text-[#3f4b52]">Quantity</Label>
            <Input
              type="number" min="1"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })}
              className="mt-1.5 bg-white border-[#cfd7db] h-11"
              data-testid="stock-qty"
            />
          </div>
          <div>
            <Label className="text-xs uppercase text-[#3f4b52]">Urgency</Label>
            <select
              value={form.urgency}
              onChange={(e) => setForm({ ...form, urgency: e.target.value })}
              className="mt-1.5 w-full h-11 bg-white border border-[#cfd7db] rounded-md px-3 text-sm"
              data-testid="stock-urgency"
            >
              {URGENCY_LEVELS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase text-[#3f4b52]">Requested by</Label>
          <Input
            value={form.requested_by}
            onChange={(e) => setForm({ ...form, requested_by: e.target.value })}
            placeholder="Your name"
            className="mt-1.5 bg-white border-[#cfd7db] h-11"
          />
        </div>
        <div>
          <Label className="text-xs uppercase text-[#3f4b52]">Notes</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            placeholder="Linked vehicle / supplier suggestion / etc."
            className="mt-1.5 bg-white border-[#cfd7db]"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="stock-submit"
          className="w-full bg-[#f28a16] text-white rounded-md py-3 font-display uppercase tracking-widest text-sm shadow inline-flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          {busy ? "Sending…" : "Send request"}
        </button>
      </form>

      <div className="mt-7 space-y-4">
        <Section title={`Pending (${pending.length})`} accent="#f28a16">
          {loading && <div className="text-sm text-[#3f4b52] p-3">Loading…</div>}
          {!loading && pending.length === 0 && <div className="text-sm text-[#3f4b52] p-3">No pending requests.</div>}
          {pending.map((r) => <StockCard key={r.id} req={r} />)}
        </Section>
        {completed.length > 0 && (
          <Section title={`Completed (${completed.length})`} accent="#10b981">
            {completed.map((r) => <StockCard key={r.id} req={r} />)}
          </Section>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, accent, children }) => (
  <div className="bg-white border border-[#cfd7db] rounded-md overflow-hidden">
    <div className="px-4 py-2.5 font-display uppercase tracking-widest text-xs flex items-center gap-2" style={{ background: `${accent}10`, color: accent }}>
      <Boxes size={14} /> {title}
    </div>
    <div className="divide-y divide-[#eef2f4]">{children}</div>
  </div>
);

const urgencyBadge = (u) => {
  const c = u === "urgent" ? "#dc2626" : u === "high" ? "#f59e0b" : "#6b7280";
  return <span className="px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-widest text-white" style={{ background: c }}>{u}</span>;
};

const StockCard = ({ req }) => (
  <div className="p-3" data-testid={`stock-${req.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-semibold text-[#111619]">{req.part_item}</div>
        <div className="text-xs text-[#3f4b52]">
          Qty {req.quantity} · {req.requested_by || "team"} · {new Date(req.created_at).toLocaleString("en-ZA")}
        </div>
        {req.notes && <div className="text-xs text-[#3f4b52] mt-1">{req.notes}</div>}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        {urgencyBadge(req.urgency)}
        {req.status === "confirmed" ? (
          <span className="inline-flex items-center gap-1 text-[#10b981] text-xs"><Check size={12} /> Bought</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[#f28a16] text-xs"><Clock size={12} /> Pending</span>
        )}
      </div>
    </div>
  </div>
);

export default TeamStock;
